import {
  BvErrorData,
  BvErrorConfig,
  BvErrorTypes,
  isKnownBvErrorType
} from './bv-error.types';

export class BvErrorConfigFactory {
  static createConfig(errorType: string, data?: BvErrorData): BvErrorConfig {
    // Validate that we know how to handle this error type

    switch (errorType) {
      case BvErrorTypes.SUGGESTED_PAYER:
        return {
          title: 'We found your info with a different insurance provider',
          subtitle: "According to what we found, it looks like you're actually covered under a different insurance.",
          primaryButtonText: 'Continue with updated info',
          secondaryLinkText: 'continue without these updates',
          showMemberDetails: true,
          showPrimaryAction: true,
          showSecondaryAction: true,
          showTertiaryAction: false
        };

      case BvErrorTypes.NO_COVERAGE:
      case BvErrorTypes.MENTAL_HEALTH_NOT_COVERED:
        return {
          title: "It looks like your plan doesn't cover mental health services",
          subtitle: 'We found your insurance plan, but it shows that mental health services are not covered.',
          primaryButtonText: 'Add a different insurance',
          secondaryLinkText: 'Continue with self-pay',
          tertiaryLinkText: 'continue with this insurance',
          showMemberDetails: false,
          showPrimaryAction: true,
          showSecondaryAction: true,
          showTertiaryAction: true
        };

      case BvErrorTypes.MEDICAID:
        return {
          title: 'Medicaid plans are not currently supported',
          subtitle: 'We found that your insurance is a Medicaid plan. '
            + "Unfortunately, we don't currently accept Medicaid plans.",
          primaryButtonText: 'Add a different insurance',
          secondaryLinkText: 'Continue with self-pay',
          showMemberDetails: false,
          showPrimaryAction: true,
          showSecondaryAction: true,
          showTertiaryAction: false
        };

      case BvErrorTypes.MEDICAID_PLAN:
        return {
          title: "We're unable to accept Medicaid",
          subtitle: 'We appreciate your interest in seeking mental health support from SonderMind. '
            + "Unfortunately, we're not in-network with Medicaid and cannot see self-pay Medicaid clients "
            + 'due to Federal regulations.',
          primaryButtonText: 'Close',
          secondaryLinkText: '',
          showMemberDetails: false,
          showPrimaryAction: true,
          showSecondaryAction: false,
          showTertiaryAction: false
        };

      case BvErrorTypes.PAYOR_DOWNTIME:
        return {
          title: "We're still verifying your benefits.",
          subtitle: "You're all set to continue booking. "
            + 'Once your benefits are verified you should receive an email and see this updated within 48 hours.',
          primaryButtonText: 'Continue',
          showMemberDetails: false,
          showPrimaryAction: true,
          showSecondaryAction: false,
          showTertiaryAction: false
        };

      case BvErrorTypes.COVERAGE_NOT_STARTED:
        return {
          title: "Your insurance coverage hasn't started yet",
          subtitle: 'We found your insurance plan, but it shows a start date in the future. '
            + "This means your insurance is not currently active and sessions won't be covered "
            + 'until your plan starts on {coverageStartDate}.',
          primaryButtonText: 'Add a different insurance',
          secondaryLinkText: 'Continue with self-pay',
          tertiaryLinkText: 'Continue with this insurance',
          showMemberDetails: false,
          showPrimaryAction: true,
          showSecondaryAction: true,
          showTertiaryAction: true
        };

      case BvErrorTypes.COVERAGE_EXPIRED:
        return {
          title: 'Your mental health coverage has ended',
          subtitle: 'It looks like coverage for your plan ended on {coverageEndDate}.',
          primaryButtonText: 'Add a different insurance',
          secondaryLinkText: 'Continue with self-pay',
          tertiaryLinkText: 'continue with this insurance',
          showMemberDetails: false,
          showPrimaryAction: true,
          showSecondaryAction: true,
          showTertiaryAction: true
        };

      case BvErrorTypes.UNKNOWN_COSTSHARE:
        return {
          title: "We've verified your health insurance!",
          subtitle: "We've verified your insurance and confirmed that you have active coverage.",
          primaryButtonText: 'Next',
          secondaryLinkText: '',
          showMemberDetails: false,
          showPrimaryAction: true,
          showSecondaryAction: false,
          showTertiaryAction: false
        };

      case BvErrorTypes.INSURANCE_OUT_OF_NETWORK:
        return {
          title: "Your insurance isn't in-network",
          subtitle: "We're unable to accept {insuranceName} at this time. "
            + 'However, you can still continue with self-pay.',
          primaryButtonText: 'Use self-pay',
          secondaryLinkText: 'Go back',
          showMemberDetails: false,
          showPrimaryAction: true,
          showSecondaryAction: true,
          showTertiaryAction: false
        };

      default:
        return {
          title: 'Insurance verification issue',
          subtitle: 'We encountered an issue while verifying your insurance information.',
          primaryButtonText: 'Continue',
          secondaryLinkText: 'try again',
          showMemberDetails: false,
          showPrimaryAction: true,
          showSecondaryAction: true,
          showTertiaryAction: false
        };
    }
  }

  static getSupportedErrorTypes(): string[] {
    return Object.values(BvErrorTypes);
  }

  static isErrorTypeSupported(errorType: string): boolean {
    return isKnownBvErrorType(errorType);
  }
}
